import { takeEvery, put, call } from "redux-saga/effects"
import { toast } from "react-toastify"

import {
  GET_ACTION_PLAN_CATEGORIES,
  ADD_ACTION_PLAN_CATEGORY,
  UPDATE_ACTION_PLAN_CATEGORY,
  DELETE_ACTION_PLAN_CATEGORY,
  GET_ACTION_PLANS,
  GET_ALL_ACTION_PLANS,
  GET_ACTION_PLAN_DETAIL,
  ADD_UPDATE_ACTION_PLAN,
  DELETE_ACTION_PLAN,
} from "./actionTypes"

import {
  getActionPlanCategoriesSuccess,
  getActionPlanCategoriesFail,
  addActionPlanCategorySuccess,
  addActionPlanCategoryError,
  updateActionPlanCategorySuccess,
  updateActionPlanCategoryFail,
  deleteActionPlanCategorySuccess,
  deleteActionPlanCategoryError,
  getActionPlansSuccess,
  getActionPlansFail,
  getAllActionPlansSuccess,
  getAllActionPlansFail,
  getActionPlanDetailSuccess,
  getActionPlanDetailFail,
  addUpdateActionPlanSuccess,
  addUpdateActionPlanError,
  deleteActionPlanSuccess,
  deleteActionPlanError,
} from "./actions"

import {
  getActionPlanCategoryApi,
  postActionPlanCategoryApi,
  updateActionPlanCategoryApi,
  deleteActionPlanCategoryApi,
  getActionPlanListApi,
  getActionPlansByCategoryApi,
  getActionPlanWithStepsApi,
  addUpdateActionPlanApi,
  deleteActionPlanApi,
} from "../../helpers/backend_helper"

//Admin master plans always use agentId = 0 (see actionTypes/url_helper notes)
const ADMIN_AGENT_ID = 0

function* fetchActionPlanCategories({ agentId }) {
  try {
    const response = yield call(getActionPlanCategoryApi, agentId)
    yield put(getActionPlanCategoriesSuccess(response))
  } catch (error) {
    yield put(getActionPlanCategoriesFail(error))
  }
}

function* onAddActionPlanCategory({ payload: { category, callback } }) {
  try {
    toast.loading("Please wait...")
    const response = yield call(postActionPlanCategoryApi, {
      agentId: ADMIN_AGENT_ID,
      actionPlanCategoryTitle: category.actionPlanCategoryTitle,
      isActive: true,
    })
    yield put(addActionPlanCategorySuccess(response))
    toast.dismiss()
    if (response && response.success === false) {
      toast.warn((response.validationErrors && response.validationErrors[0]) || "Could not save folder")
      if (callback) callback(false)
    } else {
      toast.success("Folder saved successfully")
      const res = yield call(getActionPlanCategoryApi, ADMIN_AGENT_ID)
      yield put(getActionPlanCategoriesSuccess(res))
      if (callback) callback(true)
    }
  } catch (error) {
    toast.dismiss()
    toast.error("Something went wrong")
    yield put(addActionPlanCategoryError(error))
    if (callback) callback(false)
  }
}

function* onUpdateActionPlanCategory({ payload: { category, callback } }) {
  try {
    toast.loading("Please wait...")
    const response = yield call(updateActionPlanCategoryApi, {
      id: category.id,
      agentId: ADMIN_AGENT_ID,
      actionPlanCategoryTitle: category.actionPlanCategoryTitle,
      isActive: true,
    })
    yield put(updateActionPlanCategorySuccess(response))
    toast.dismiss()
    toast.success("Folder updated successfully")
    const res = yield call(getActionPlanCategoryApi, ADMIN_AGENT_ID)
    yield put(getActionPlanCategoriesSuccess(res))
    if (callback) callback(true)
  } catch (error) {
    toast.dismiss()
    toast.error("Something went wrong")
    yield put(updateActionPlanCategoryFail(error))
    if (callback) callback(false)
  }
}

function* onDeleteActionPlanCategory({ payload: category }) {
  try {
    toast.loading("Please wait...")
    const response = yield call(deleteActionPlanCategoryApi, category.id)
    yield put(deleteActionPlanCategorySuccess(response))
    toast.dismiss()
    toast.success("Folder deleted successfully")
    const res = yield call(getActionPlanCategoryApi, ADMIN_AGENT_ID)
    yield put(getActionPlanCategoriesSuccess(res))
  } catch (error) {
    toast.dismiss()
    toast.error("Something went wrong")
    yield put(deleteActionPlanCategoryError(error))
  }
}

// API may return either a flat plan list, or [{ actionPlans: [...] }]
function normalizePlansByCategoryResponse(response) {
  if (!Array.isArray(response)) return []
  if (response.length && Array.isArray(response[0]?.actionPlans)) {
    return response[0].actionPlans
  }
  return response
}

function* fetchActionPlans({ agentId, folder }) {
  try {
    const response = yield call(getActionPlansByCategoryApi, agentId, folder)
    yield put(getActionPlansSuccess(normalizePlansByCategoryResponse(response)))
  } catch (error) {
    yield put(getActionPlansFail(error))
  }
}

function* fetchAllActionPlans({ agentId }) {
  try {
    const response = yield call(getActionPlanListApi, agentId)
    yield put(getAllActionPlansSuccess(response))
  } catch (error) {
    yield put(getAllActionPlansFail(error))
  }
}

function* fetchActionPlanDetail({ id }) {
  try {
    const response = yield call(getActionPlanWithStepsApi, id)
    yield put(getActionPlanDetailSuccess(response))
  } catch (error) {
    yield put(getActionPlanDetailFail(error))
  }
}

function* onAddUpdateActionPlan({ payload: { plan, callback } }) {
  try {
    toast.loading("Please wait...")
    const response = yield call(addUpdateActionPlanApi, plan)
    yield put(addUpdateActionPlanSuccess(response))
    toast.dismiss()
    if (response && response.success === false) {
      toast.warn((response.validationErrors && response.validationErrors[0]) || "Could not save action plan")
      if (callback) callback(false)
    } else {
      toast.success("Action plan saved successfully")
      if (callback) callback(true, response)
    }
  } catch (error) {
    toast.dismiss()
    toast.error("Something went wrong")
    yield put(addUpdateActionPlanError(error))
    if (callback) callback(false)
  }
}

function* onDeleteActionPlan({ payload: plan }) {
  try {
    toast.loading("Please wait...")
    const response = yield call(deleteActionPlanApi, plan.id)
    yield put(deleteActionPlanSuccess(response))
    toast.dismiss()
    toast.success("Action plan deleted successfully")
    const res = yield call(getActionPlansByCategoryApi, ADMIN_AGENT_ID, plan.actionPlanCategory)
    yield put(getActionPlansSuccess(normalizePlansByCategoryResponse(res)))
  } catch (error) {
    toast.dismiss()
    toast.error("Something went wrong")
    yield put(deleteActionPlanError(error))
  }
}

function* ActionPlanSaga() {
  yield takeEvery(GET_ACTION_PLAN_CATEGORIES, fetchActionPlanCategories)
  yield takeEvery(ADD_ACTION_PLAN_CATEGORY, onAddActionPlanCategory)
  yield takeEvery(UPDATE_ACTION_PLAN_CATEGORY, onUpdateActionPlanCategory)
  yield takeEvery(DELETE_ACTION_PLAN_CATEGORY, onDeleteActionPlanCategory)
  yield takeEvery(GET_ACTION_PLANS, fetchActionPlans)
  yield takeEvery(GET_ALL_ACTION_PLANS, fetchAllActionPlans)
  yield takeEvery(GET_ACTION_PLAN_DETAIL, fetchActionPlanDetail)
  yield takeEvery(ADD_UPDATE_ACTION_PLAN, onAddUpdateActionPlan)
  yield takeEvery(DELETE_ACTION_PLAN, onDeleteActionPlan)
}

export default ActionPlanSaga
